import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';

export const DEFAULT_COUNTRY: CountryCode = 'US';

/** National format for the workspace country, international otherwise. E.164 is kept for copy/tooltips. */
export function formatPhone(e164: string | null | undefined, country: CountryCode = DEFAULT_COUNTRY): string {
  if (!e164) return 'Unknown number';
  const parsed = parsePhoneNumberFromString(e164);
  if (!parsed) return e164;
  return parsed.country === country ? parsed.formatNational() : parsed.formatInternational();
}

export type PhoneParse =
  | { ok: true; e164: string; display: string }
  | { ok: false; reason: 'empty' | 'invalid' | 'too_short' | 'not_number' };

/** Normalises typed or pasted input to E.164 with libphonenumber; never a hand-written regex. */
export function parsePhoneInput(input: string, country: CountryCode = DEFAULT_COUNTRY): PhoneParse {
  const trimmed = input.trim();
  if (!trimmed) return { ok: false, reason: 'empty' };
  if (/[a-z]/i.test(trimmed.replace(/ext\.?|x/gi, ''))) return { ok: false, reason: 'not_number' };
  const parsed = parsePhoneNumberFromString(trimmed, country);
  if (!parsed) return { ok: false, reason: 'too_short' };
  if (!parsed.isValid()) return { ok: false, reason: 'invalid' };
  return { ok: true, e164: parsed.number, display: formatPhone(parsed.number, country) };
}

export function phoneParseMessage(result: Exclude<PhoneParse, { ok: true }>): string {
  switch (result.reason) {
    case 'empty':
      return 'Enter a phone number.';
    case 'not_number':
      return 'Use digits only, for example (669) 555-0100 or +44 20 7946 0000.';
    case 'too_short':
      return 'That number is too short.';
    default:
      return "That number doesn't look valid. Include the country code for numbers outside the US.";
  }
}

/** m:ss under an hour, h:mm:ss above (UX spec 3.8). */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}

export function formatAbsolute(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short', ...(timeZone ? { timeZone } : {}) }).format(date);
}

export function formatClock(epochMs: number): string {
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' }).format(new Date(epochMs));
}

/** "4 min ago" style; callers put formatAbsolute() in the title and accessible name. */
export function formatRelative(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const seconds = Math.round((then - now) / 1000);
  const abs = Math.abs(seconds);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto', style: 'short' });
  if (abs < 45) return 'just now';
  if (abs < 3600) return rtf.format(Math.round(seconds / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(seconds / 3600), 'hour');
  if (abs < 86400 * 7) return rtf.format(Math.round(seconds / 86400), 'day');
  return formatAbsolute(iso);
}

export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}
